import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import Button from '~components/Button';
import SecretCodeBlock from '~components/SecretCodeBlock';

import { CompanyAccessKey } from '~main/models';

import { useCreateCompanyAccessKeyMutation } from '~main/actions/companyAccessKeys';

import ModalForm from './ModalForm';

interface ComponentProps {
  isOpen: boolean;
  toggle: () => void;
  onRevoke: (row: Partial<CompanyAccessKey>) => void;
  isNew: boolean;
  rowData?: CompanyAccessKey;
}

const newAccessKey: Partial<CompanyAccessKey> = {
  name: '',
  expires_at: '',
  tags: [],
};

const AccessKeyModal: React.FC<ComponentProps> = ({ isOpen, toggle, onRevoke, isNew, rowData }) => {
  const [formData, setFormData] = useState<Partial<CompanyAccessKey>>(newAccessKey);
  const [secretModalInfo, setSecretModalInfo] = useState<{
    isOpen: boolean;
    accessKey: string;
    accessToken: string;
  }>({
    isOpen: false,
    accessKey: '',
    accessToken: '',
  });

  const [createAccessKey, { isLoading: isCreating }] = useCreateCompanyAccessKeyMutation();

  useEffect(() => {
    if (isOpen) {
      if (isNew) {
        setFormData(newAccessKey);
      } else if (rowData) {
        setFormData({
          id: rowData.id,
          name: rowData.name,
          expires_at: rowData.expires_at,
          tags: rowData.tags || [],
          access_key: rowData.access_key,
        });
      }
    }
  }, [isOpen, isNew, rowData]);

  const handleFieldChange = (name: string, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      return newData;
    });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.expires_at || !formData.tags || formData.tags.length === 0) return;

    const body = {
      name: formData.name,
      expires_at: formData.expires_at,
      tags: formData.tags,
    };

    const { success, data } = await createAccessKey(body).unwrap();

    if (success) {
      setSecretModalInfo({ isOpen: true, accessKey: data.access_key, accessToken: data.access_token });
    }
  };

  const handleSecretInfoClose = () => {
    toggle();
    setSecretModalInfo({ isOpen: false, accessKey: '', accessToken: '' });
  };

  const isSubmitValid = !!formData.name && !!formData.expires_at && !!formData.tags && formData.tags.length > 0;

  const headerMessage = isNew ? 'Create New Access Key' : `Viewing Access Key - ${rowData?.name || ''}`;

  return (
    <>
      <Modal isOpen={isOpen} size={isNew ? 'lg' : 'md'}>
        <ModalHeader toggle={toggle}>{headerMessage}</ModalHeader>
        <ModalBody>
          <ModalForm isNew={isNew} handleFieldChange={handleFieldChange} values={formData} />
        </ModalBody>
        <ModalFooter>
          {isNew && (
            <>
              <Button onClick={toggle} color="danger">
                Cancel
              </Button>
              <Button onClick={handleSubmit} color="success" disabled={!isSubmitValid || isCreating}>
                Submit
              </Button>
            </>
          )}
          {!isNew && rowData && (
            <Button onClick={() => onRevoke({ ...rowData, id: rowData.id })} color="danger">
              Revoke
            </Button>
          )}
        </ModalFooter>
      </Modal>
      <Modal isOpen={secretModalInfo.isOpen} size="md">
        <ModalHeader>Access Key Created</ModalHeader>
        <ModalBody>
          <div className="text-center">
            <strong>This will be the only time you will see this token.</strong>
          </div>
          <div className="mb-4 text-center">
            <strong>Please ensure it is saved somewhere before proceeding.</strong>
          </div>
          <div className="row mb-2">
            <SecretCodeBlock secret={secretModalInfo.accessKey} label="Access Key" masked={false} />
          </div>
          <div className="row">
            <SecretCodeBlock secret={secretModalInfo.accessToken} label="Access Token" />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={handleSecretInfoClose}>
            Confirm & Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AccessKeyModal;
